import React, { useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { useForm, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Grid, Button, Tooltip, TableRow, TableCell } from '@mui/material'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import { CopyToClipboardIcon } from 'assets/icons'
import InputField from 'components/form/InputField'
import GenericSelect from 'components/form/GenericSelect'
import SubmitButton from 'components/form/SubmitButton'
import ErrorMessage from 'components/ui/ErrorMessage'

import { searchUsers } from 'store/users/thunks'
import { saveSearchFilters, selectUsers, updateUsers } from 'store/users/slice'
import { selectLocale } from 'store/locale/slice'
import { convertHcpToString } from 'utils/convertHcpToString'
import InfiniteScrollTable from 'components/ui/InfiniteScrollTable'
import { ClientRoute } from 'routes/client'
import { sendSuccessNotification } from 'utils/notifications'

export default function UserSearch(): JSX.Element {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { users, status, searchFilters } = useSelector(selectUsers)
  const locale = useSelector(selectLocale)
  const initialFormData: UserSearchForm = useMemo(() => {
    return { name: '', email: '', account: '', userId: '', active: 'true', registered: 'true' }
  }, [])
  const { register, handleSubmit, control, reset, getValues } = useForm<UserSearchForm>({
    defaultValues: initialFormData,
  })
  const watch = useWatch({ control })
  const [additionalDropdownFieldValue, setAdditionalDropdownFieldValue] = useState<string>('gender')
  const [noFiltersAlert, setNoFiltersAlert] = useState<string | null>(null)
  const [sortKey, setSortKey] = useState<string | undefined>(undefined)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const searchLimit = 50

  useEffect(() => {
    const filtersSaved = Object.keys(searchFilters).length > 0
    if (filtersSaved) {
      reset(searchFilters)
    }
  }, [searchFilters, reset])

  const activeOptions: SelectOption[] = [
    { key: 'true', label: intl.formatMessage({ id: 'strings.active' }) },
    { key: 'false', label: intl.formatMessage({ id: 'strings.inactive' }) },
    { key: 'both', label: intl.formatMessage({ id: 'strings.both' }) },
  ]

  const registeredOptions: SelectOption[] = [
    { key: 'true', label: intl.formatMessage({ id: 'strings.registered' }) },
    { key: 'false', label: intl.formatMessage({ id: 'strings.unregistered' }) },
    { key: 'both', label: intl.formatMessage({ id: 'strings.both' }) },
  ]

  const handleAdditionalDropdownFieldValue = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAdditionalDropdownFieldValue(e.target.value)
  }

  const columns: TableColumn[] = [
    {
      id: 'fullName',
      label: intl.formatMessage({ id: 'strings.name' }),
      width: 15,
      sortable: true,
    },
    {
      id: 'email',
      label: intl.formatMessage({ id: 'strings.email' }),
      width: 20,
      sortable: true,
    },
    {
      id: 'account',
      label: intl.formatMessage({ id: 'strings.username' }),
      width: 15,
      sortable: true,
    },
    {
      id: 'id',
      label: intl.formatMessage({ id: 'strings.userId' }),
      width: 10,
      sortable: true,
    },
    {
      id: 'country',
      label: intl.formatMessage({ id: 'strings.country' }),
      width: 10,
      sortable: true,
    },
    {
      id: 'club',
      label: intl.formatMessage({ id: 'strings.homeClub' }),
      width: 10,
      sortable: true,
    },
    {
      id: 'handicap',
      label: intl.formatMessage({ id: 'strings.hcp' }),
      width: 5,
      sortable: true,
    },
    {
      id: 'additionalFields',
      label: (
        <AdditionalDropdownSelect onChange={handleAdditionalDropdownFieldValue}>
          <option value="gender">{intl.formatMessage({ id: 'strings.gender' })}</option>
          <option value="dateOfBirth">{intl.formatMessage({ id: 'strings.birthDate' })}</option>
          <option value="regDate">{intl.formatMessage({ id: 'strings.registrationDate' })}</option>
          <option value="playedRounds">{intl.formatMessage({ id: 'strings.playedRounds' })}</option>
        </AdditionalDropdownSelect>
      ),
      width: 15,
    },
  ]

  const sortHandler = (key: string) => {
    let sortDir: 'asc' | 'desc' = 'asc'
    if (sortKey !== key) {
      setSortKey(key)
      setSortDirection('asc')
    } else {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc'
      sortDir = newDirection
      setSortDirection(newDirection)
    }
    const sortedUsers = users.slice().sort((u1, u2) => sortCompare(u1, u2, key, sortDir))
    dispatch(updateUsers(sortedUsers))
  }

  const sortCompare = (a: User, b: User, key: string, direction: 'asc' | 'desc') => {
    const firstValue = direction === 'asc' ? a[key] : b[key]
    const secondValue = direction === 'asc' ? b[key] : a[key]
    const isNumeric = !isNaN(parseInt(firstValue)) || !isNaN(parseInt(secondValue))
    if (isNumeric && firstValue !== secondValue) {
      return firstValue > secondValue ? 1 : -1
    }

    const isObject = typeof firstValue === 'object' || typeof secondValue === 'object'
    if (isObject) {
      const first = firstValue ? firstValue.name : ''
      const second = secondValue ? secondValue.name : ''
      return first.localeCompare(second)
    }

    if (!isNumeric) {
      const first = firstValue || ''
      const second = secondValue || ''
      return first.localeCompare(second)
    }

    return 0
  }

  const getAdditionalDropdownFieldValue = (user: User) => {
    const isDate = ['dateOfBirth', 'regDate'].includes(additionalDropdownFieldValue)
    const value = user[additionalDropdownFieldValue]
    if (isDate && value) {
      const dateTime = new Date(value.date)
      return new Intl.DateTimeFormat(locale.language.code).format(dateTime)
    }
    return value
  }

  const handleCopyToClipboard = (value: string | number) => {
    const notificationMessage = `${value} ${intl.formatMessage({
      id: 'strings.copiedToClipboard',
    })}`
    sendSuccessNotification(notificationMessage)
  }

  const renderCellWithCopyToClipBoard = (value: string | number | null | undefined) => {
    if (!value) {
      return
    }
    return (
      <CellContainer>
        {renderTooltip(value)}
        <CopyToClipboard text={value} onCopy={() => handleCopyToClipboard(value)}>
          <span>
            <CopyToClipboardIcon />
          </span>
        </CopyToClipboard>
      </CellContainer>
    )
  }

  const renderTooltip = (value: string | number | null | undefined) => {
    if (!value) {
      return
    }
    return (
      <Tooltip title={value}>
        <span>{value}</span>
      </Tooltip>
    )
  }

  const renderName = (user: User) => {
    return (
      <>
        {renderTooltip(user.fullName)}
        {user.isGoldUser && <GoldBadge>GOLD</GoldBadge>}
      </>
    )
  }

  const submitSearchForm = (data: UserSearchForm) => {
    setAdditionalDropdownFieldValue('gender')
    setNoFiltersAlert(null)

    const payload = generatePayload(data, 0)
    const { name, email, account, userId } = payload

    const atLeastOneFilterGiven = !!name || !!email || !!account || !!userId
    if (atLeastOneFilterGiven) {
      dispatch(searchUsers(payload))
    } else {
      setNoFiltersAlert(intl.formatMessage({ id: 'strings.selectAtLeastOneFilter' }))
    }
  }

  const loadMoreResults = () => {
    if (status !== 'loading') {
      const payload = generatePayload(getValues(), users.length)
      dispatch(searchUsers(payload))
    }
  }

  const generatePayload = (data: any, offset: number): UserSearchPayload => {
    dispatch(saveSearchFilters(data))
    const { name, email, account, userId, active, registered } = data

    const optionValues = [
      { key: 'true', value: true },
      { key: 'false', value: false },
      { key: 'both', value: null },
    ]

    return {
      name: name || null,
      email: email || null,
      account: account || null,
      userId: userId ? parseInt(userId) : null,
      active: optionValues.filter((option) => option.key === active)[0]?.value,
      registered: optionValues.filter((option) => option.key === registered)[0]?.value,
      limit: searchLimit,
      offset: offset,
    }
  }

  const clearFilters = () => {
    reset(initialFormData)
    setNoFiltersAlert(null)
    dispatch(saveSearchFilters({}))
  }

  const maybeRenderResults = (): JSX.Element[] => {
    if (users.length === 0 && status === 'succeeded') {
      return [<NoResults>{intl.formatMessage({ id: 'strings.noResults' })}</NoResults>]
    }

    return users.map((user: User, i: number) => (
      <TableRow hover key={i}>
        <TableCell>
          <Link to={ClientRoute.ADMIN_USER(user.id || 0)}>{renderName(user)}</Link>
        </TableCell>
        <TableCell>{renderCellWithCopyToClipBoard(user.email)}</TableCell>
        <TableCell>{renderCellWithCopyToClipBoard(user.account)}</TableCell>
        <TableCell>{renderCellWithCopyToClipBoard(user.id)}</TableCell>
        <TableCell>{renderTooltip(user.country?.name)}</TableCell>
        <TableCell>{renderTooltip(user.club?.name)}</TableCell>
        <TableCell>{convertHcpToString(user.handicap)}</TableCell>
        <TableCell>
          <AdditionalFieldValueContainer>{getAdditionalDropdownFieldValue(user)}</AdditionalFieldValueContainer>
        </TableCell>
      </TableRow>
    ))
  }

  return (
    <PageContainer>
      <PageTitle>{intl.formatMessage({ id: 'strings.searchUsers' })}</PageTitle>
      <SearchForm onSubmit={handleSubmit(submitSearchForm)}>
        <Grid container spacing={5} style={{ justifyContent: 'center' }}>
          <Grid item xs={2}>
            <InputField
              register={register}
              name="name"
              type="text"
              label={intl.formatMessage({ id: 'strings.name' })}
              outlined
              autoFocus
            />
          </Grid>
          <Grid item xs={2}>
            <InputField
              register={register}
              name="email"
              type="text"
              label={intl.formatMessage({ id: 'strings.email' })}
              outlined
            />
          </Grid>
          <Grid item xs={2}>
            <InputField
              register={register}
              name="account"
              type="text"
              label={intl.formatMessage({ id: 'strings.username' })}
              outlined
            />
          </Grid>

          <Grid item xs={2}>
            <InputFieldNumber
              register={register}
              name="userId"
              type="number"
              label={intl.formatMessage({ id: 'strings.userId' })}
              outlined
            />
          </Grid>

          <Grid item xs={2}>
            <ActiveSelection
              name="active"
              control={control}
              options={activeOptions}
              initialValue={watch['active']}
              label={intl.formatMessage({ id: 'strings.active' })}
            />
          </Grid>

          <Grid item xs={2}>
            <RegisteredSelection
              name="registered"
              control={control}
              options={registeredOptions}
              initialValue={watch['registered']}
              label={intl.formatMessage({ id: 'strings.registered' })}
            />
          </Grid>

          <Grid item xs={6} style={{ textAlign: 'right' }}>
            <SearchButton
              status={users.length === 0 ? status : 'idle'}
              text={intl.formatMessage({ id: 'strings.search' })}
            />
          </Grid>

          <Grid item xs={6}>
            <ClearFilters onClick={clearFilters}>{intl.formatMessage({ id: 'strings.clearFilters' })}</ClearFilters>
            {users?.length > 0 && (
              <SearchResultCount>
                {users.length} {intl.formatMessage({ id: 'strings.results' })}
              </SearchResultCount>
            )}
          </Grid>

          <Grid item xs={12}>
            {noFiltersAlert && <ErrorMessage>{noFiltersAlert}</ErrorMessage>}
          </Grid>
        </Grid>
      </SearchForm>

      <SearchResults>
        <InfiniteScrollTable
          columns={columns}
          rowCount={users.length}
          hasMoreRows={users.length >= searchLimit && users.length % searchLimit === 0}
          loadMore={loadMoreResults}
          noMoreRowsText={users.length > 0 ? intl.formatMessage({ id: 'strings.noMoreResults' }) : ''}
          status={status}
          sortHandler={sortHandler}
          sortDirection={sortDirection}
          sortByKey={sortKey}
        >
          {maybeRenderResults()}
        </InfiniteScrollTable>
      </SearchResults>
    </PageContainer>
  )
}

const PageContainer = styled.div`
  margin: 10px;
  padding: 10px 20px 20px;
  min-height: 500px;
  width: 100%;
  background-color: ${(props) => props.theme.customPalette.light};

  & a {
    color: ${(props) => props.theme.palette.primary.main};
    text-decoration: none;
  }
`

const PageTitle = styled.h1`
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 26px;
`

const SearchForm = styled.form`
  padding: 20px 0;
`

const ActiveSelection = styled(GenericSelect)`
  margin: 20px 0 0 0;
`

const RegisteredSelection = styled(ActiveSelection)``

const SearchButton = styled(SubmitButton)`
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
`

const ClearFilters = styled(Button)`
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.palette.primary.main};
`

const AdditionalDropdownSelect = styled.select`
  background-color: transparent;
  color: ${(props) => props.theme.customPalette.light};
  border: 1px solid;
  border-color: ${(props) => props.theme.customPalette.light};
  padding: 5px;
  font-size: 0.95em;
  text-transform: uppercase;
  max-width: 90%;
`

const AdditionalFieldValueContainer = styled.div`
  text-transform: capitalize;
`

const SearchResultCount = styled.div`
  display: inline-block;
  padding-left: 20px;
  font-size: 0.9em;
`

const NoResults = styled.div`
  display: flex;
  justify-content: center;
`

const CellContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  & > span:first-of-type {
    width: 80%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  & > span:nth-of-type(2) {
    cursor: pointer;
    padding-left: 5px;
    width: 20%;
    min-width: 40px;
    max-width: 40px;
  }
`

const InputFieldNumber = styled(InputField)`
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type='number'] {
    -moz-appearance: textfield;
  }
`

const SearchResults = styled.div`
  background-color: ${(props) => props.theme.customPalette.light};
  padding: 20px 0;
  margin-bottom: 10px;

  & table {
    table-layout: fixed;
    max-width: 100%;
  }
`

const Badge = styled.span`
  color: #fff;
  border-radius: 2px;
  font-weight: bold;
  font-size: 10px;
  line-height: 14px;
  text-align: center;
  padding: 2px 5px;
  margin-left: 5px;
`

const GoldBadge = styled(Badge)`
  background-color: #ffd700;
`
